import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Cell, Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import useNpsScoreColor from 'src/components/dashboard/hooks/useNpsScoreColor';
import type { StackedNumbersList } from 'src/components/followUp/common/types';

import type { BarTableCellData } from './components/BarTable/BarTableCellWithHiddenBar';
import {
  getStackLabel,
  HIDDEN_BARS,
  labelTranslationExists,
  SENTIMENT_NAMES,
  transformResults,
} from './helper';

export const useGetColorBasedOnLabelAndValue = () => {
  const { colors } = useTheme();
  const getNpsScoreColor = useNpsScoreColor();

  return useCallback(
    (label: string, value: number) => {
      switch (label) {
        case 'passive':
          return colors.neutral[500];
        case 'detractor':
          return colors.error[600];
        case 'promoter':
          return colors.tertiary[700];
        case 'netPromoterScore':
          return getNpsScoreColor(value);
        case 'open':
        case 'closed':
        case 'overDue':
        case 'inProgress':
        case 'totalDetractors':
          return colors.black;
        default:
          return alpha(colors.tertiary[700], 0.1);
      }
    },
    [colors],
  );
};

export const useGenerateAggregateLabels = (
  addRelativeLabelsForQuestions?: boolean,
) => {
  const { t, i18n } = useTranslation(['followUp']);

  return useCallback(
    (data?: StackedNumbersList) =>
      data?.results[0]?.stacks
        .filter((stack) => stack.stackLabel !== 'total')
        .map((stack) =>
          getStackLabel(
            stack.stackLabel,
            t,
            i18n,
            addRelativeLabelsForQuestions,
          ),
        ) ?? [],
    [t, i18n, addRelativeLabelsForQuestions],
  );
};

export const useTransformData = (data: StackedNumbersList) => {
  const { t, i18n } = useTranslation(['followUp', 'common']);
  const aggregateLabels = useGenerateAggregateLabels();
  const getColor = useGetColorBasedOnLabelAndValue();

  const dataTransformed = useMemo(() => transformResults(data), [data]);
  const labels = useMemo(
    () => aggregateLabels(dataTransformed),
    [dataTransformed, aggregateLabels],
  );
  const dataCalculated = useMemo(
    () =>
      dataTransformed?.results.map((result) => {
        const totalForUnit = result.stacks.find(
          (stack) => stack.stackLabel === 'total',
        )?.value;

        return {
          ...result,
          stacks: result.stacks.reduce((acc: BarTableCellData[], stack) => {
            if (stack.stackLabel === 'total') return acc;

            // @ts-expect-error TS7053
            const totalValue = stack[SENTIMENT_NAMES.total] ?? totalForUnit;
            // @ts-expect-error TS7053
            const value = stack[SENTIMENT_NAMES.positive] ?? stack.value;

            return [
              ...acc,
              {
                color: getColor(stack.stackLabel, stack.value),
                value: value,
                comparison: null,
                centeredScale: stack.stackLabel === 'netPromoterScore',
                stackLabel: getStackLabel(stack.stackLabel, t, i18n).toString(),
                hideBar: HIDDEN_BARS.includes(stack.stackLabel),
                percentage: labelTranslationExists(stack.stackLabel, i18n)
                  ? null
                  : getRelativeValue(value, totalValue, 0),
              },
            ];
          }, []),
        };
      }),
    [dataTransformed, i18n, t, aggregateLabels, getColor],
  );

  return { dataCalculated, labels };
};

const useMapRows = () => {
  const { t, i18n } = useTranslation('followUp');
  const generateAggregateLabels = useGenerateAggregateLabels(true);

  return useCallback(
    (data: StackedNumbersList | null): Row[] => {
      if (!data) return [];
      const aggregateLabels = generateAggregateLabels(data);

      return [
        [
          { value: t('statistics.orgUnit'), fontWeight: 'bold' },
          ...aggregateLabels.flatMap<Cell>((label) =>
            Array.isArray(label)
              ? [
                  ...label.map<Cell>((flatLabel) => ({
                    value: flatLabel,
                    fontWeight: 'bold',
                  })),
                ]
              : [
                  {
                    value: label,
                    fontWeight: 'bold',
                  },
                ],
          ),
        ],
        ...data.results.map<Row>((row) => {
          const rowTotal =
            row.stacks.find((stack) => stack.stackLabel === 'total')?.value ??
            0;

          return [
            {
              value: row.label,
            },
            ...row.stacks
              .filter((stack) => stack.stackLabel !== 'total')
              .flatMap<Cell>((stack) => {
                const isStatement = !labelTranslationExists(
                  stack.stackLabel,
                  i18n,
                );
                return isStatement
                  ? [
                      { value: stack.value },
                      {
                        value: `${getRelativeValue(stack.value, rowTotal, 0)}%`,
                        align: 'right',
                      },
                    ]
                  : [
                      {
                        value: stack.value,
                      },
                    ];
              }),
          ];
        }),
      ];
    },
    [t, i18n],
  );
};

type UseExportOptionsProps = {
  data: StackedNumbersList | null;
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

export const useExportOptions = ({
  data,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const mapRows = useMapRows();

  return useMemo(() => {
    const rows = mapRows(data);

    return {
      widgetName,
      rows,
      queryFilters,
    };
  }, [data, widgetName, queryFilters, mapRows]);
};
